import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Wallet, CalendarDays, Clock, CloudSun,
  Newspaper, Images, Contact, Megaphone, Settings as SettingsIcon,
  LogOut, Languages, Menu, X, UserCircle, Home,
} from "lucide-react";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";

const NAV = [
  { to: "/", key: "home", icon: Home, roles: ["admin", "staff", "parent"], end: true },
  { to: "/app", key: "dashboard", icon: LayoutDashboard, roles: ["admin", "staff", "parent"], end: true },
  { to: "/app/players", key: "players", icon: Users, roles: ["admin", "staff", "parent"] },
  { to: "/app/finance", key: "finance", icon: Wallet, roles: ["admin", "staff", "parent"] },
  { to: "/app/schedule", key: "schedule", icon: CalendarDays, roles: ["admin", "staff", "parent"] },
  { to: "/app/attendance", key: "attendance", icon: Clock, roles: ["admin", "staff"] },
  { to: "/app/inquiries", key: "inquiries", icon: CloudSun, roles: ["admin", "staff", "parent"] },
  { to: "/app/news", key: "news", icon: Newspaper, roles: ["admin", "staff", "parent"] },
  { to: "/app/gallery", key: "gallery", icon: Images, roles: ["admin", "staff", "parent"] },
  { to: "/app/directory", key: "directory", icon: Contact, roles: ["admin", "staff", "parent"] },
  { to: "/app/profile", key: "profile", icon: UserCircle, roles: ["admin", "staff", "parent"] },
  { to: "/app/announcements", key: "announcements", icon: Megaphone, roles: ["admin", "staff"] },
  { to: "/app/settings", key: "settings", icon: SettingsIcon, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, toggle, lang } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navLinks = (onClick) =>
    items.map((n) => (
      <NavLink
        key={n.to}
        to={n.to}
        end={n.end}
        onClick={onClick}
        data-testid={`nav-${n.key}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
            isActive ? "bg-[#84CC16] text-[#0A0A0A]" : "text-white/80 hover:bg-white/10 hover:text-white"
          }`
        }
      >
        <n.icon size={18} />
        {t(n.key)}
      </NavLink>
    ));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-[#114724] md:flex">
        <div className="flex items-center gap-3 px-6 py-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#84CC16] font-['Outfit'] text-lg font-800 font-bold text-[#0A0A0A]">G</div>
          <div>
            <p className="font-['Outfit'] text-sm font-bold text-white leading-tight">Glocal Football</p>
            <p className="text-xs text-[#84CC16]">Academy</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3" data-testid="sidebar-nav">{navLinks()}</nav>
        <div className="border-t border-white/10 p-4">
          <p className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="text-xs uppercase tracking-wide text-[#84CC16]" data-testid="sidebar-user-role">{user?.role}</p>
        </div>
      </aside>

      <div className="md:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/70 px-4 py-3 backdrop-blur-xl md:px-8">
          <button className="md:hidden text-[#165A2E]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <p className="hidden font-['Outfit'] text-lg font-bold text-[#165A2E] md:block">{t("appName")}</p>
          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="flex items-center gap-1.5 rounded-full border border-[#165A2E]/30 px-4 py-1.5 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E] hover:text-white"
            >
              <Languages size={15} />
              {lang === "ms" ? "EN" : "MS"}
            </button>
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="flex items-center gap-1.5 rounded-full bg-[#165A2E] px-4 py-1.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#0d3a1e]"
            >
              <LogOut size={15} />
              {t("logout")}
            </button>
          </div>
        </header>

        {mobileOpen && (
          <div className="z-10 space-y-1 bg-[#114724] p-3 md:hidden" data-testid="mobile-nav">{navLinks(() => setMobileOpen(false))}</div>
        )}

        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
